<!-- eslint-disable vue/multi-word-component-names -->
<!-- eslint-disable no-undef -->
<script setup>
  // Imports
  import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
  import { useRouter } from 'vue-router';
  import { useStore } from 'vuex';
  import { doc, getDoc, updateDoc } from 'firebase/firestore';
  import { PlusIcon, EllipsisVerticalIcon } from '@heroicons/vue/24/solid';
  import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
  import draggable from 'vuedraggable';

  import { db } from '@/firebase';
  import { timeSimplified, utcToLocal } from '@/tools/TimeFunctions';
  import Device from '@/api/device';
  import { hasAnyPermission } from '@/tools/PermissionChecker';
  import AddDashboardPage from '@/components/modals/AddDashboardPage.vue';
  import { useModalBackdropClose } from '@/composables/useModalBackdropClose';
  import {
    isCloudAppPortalUnit,
    cloudAppPortalTabLabel,
    cloudAppPortalPath,
    unitManagementPath,
    defaultUnitFallbackPath,
  } from '@/tools/cloudAppUnit';

  // Definitions
  defineOptions({
    name: 'DefaultTitle'
  })

  const emit = defineEmits(['update:deviceDetails', 'update:ready']);

  const props = defineProps({
    dataID: {
      type: String,
      required: false
    },
    title: {
      type: String,
      required: false,
      default: ''
    }
  })

  // References
  const internalTitle = ref(props.title);
  const subtitle = ref(null);
  const links = ref(new Map());
  const subtitleUpdateInterval = ref(null);
  const error = ref(null);
  const templateData = ref(null);
  const deviceDetails = ref(null);
  const unsubscribeStream = ref(null);
  const showAddPageModal = ref(false);
  const showRenameModal = ref(false);
  const showDeleteConfirm = ref(false);
  const selectedPageId = ref(null);
  const newPageName = ref('');
  const draggablePages = ref([]);
  /** Last nav tab (Management or “{cloudApp} Portal”) - not keyed as “Management” in links when portal. */
  const unitSecondaryTab = ref(null);

  const closeRenameModal = () => { showRenameModal.value = false };
  const closeDeleteConfirm = () => { showDeleteConfirm.value = false };
  const renameBackdrop = useModalBackdropClose(closeRenameModal);
  const deleteBackdrop = useModalBackdropClose(closeDeleteConfirm);

  const router = useRouter();
  const store = useStore();
  const timeFormat = store.state.user.timeFormat;
  const entityData = store.state.user.entityData || {};
  const subEntities = store.state.user.entityData[store.state.user.entity]?.subEntities || [];

  // Compute subentities for this device
  const deviceSubEntities = computed(() => {
    if (!deviceDetails.value?.entities || !subEntities || !entityData) return [];
    
    return subEntities
      .filter(subEntityId => deviceDetails.value.entities.includes(subEntityId))
      .map(subEntityId => ({
        id: subEntityId,
        name: entityData[subEntityId]?.name || subEntityId,
        ...entityData[subEntityId]
      }))
      .filter(entity => entity.name);
  });

  const isTemplateEditable = computed(() => hasAnyPermission(['editTemplate']));

  // Lifecycle hooks
  onMounted(async () => {
    if (props.dataID) {
      try {
        // Check if the device details are already in the store
        deviceDetails.value = store.state.device;
        if (!deviceDetails.value || deviceDetails.value.dataID !== props.dataID) {
            deviceDetails.value = await getDeviceDetails();
        } else {
          getDeviceDetails();
        }

        // Check if the template is already in the store
        templateData.value = store.state.template;
        if (!templateData.value || templateData.value.dataID !== props.dataID) {
          templateData.value = await getTemplate();
        } else {
          getTemplate();
        }

        setSubtitle();
        setSubLinks();
        emit('update:ready');
        emit('update:deviceDetails', deviceDetails.value);

        // Live updates: mesh API SSE (Cloud Function GET /device/stream/ is not deployed - 404)
        let eventSource = null;
        try {
          eventSource = await Device.stream();
          unsubscribeStream.value = () => {
            if (eventSource) {
              eventSource.close();
              eventSource = null;
            }
          };

          const applyDeviceStreamEvent = (streamEvent) => {
            try {
              const parsed = JSON.parse(streamEvent.data);
              const devices = Array.isArray(parsed) ? parsed : (parsed && typeof parsed === 'object' ? [parsed] : []);
              const mine = devices.find((d) => d && d.id === props.dataID);
              if (!mine) return;
              const rest = { ...mine };
              delete rest.id;
              const next = { ...deviceDetails.value, ...rest, dataID: props.dataID };
              deviceDetails.value = next;
              store.dispatch('setDevice', next);
              setSubtitle();
              setSubLinks();
            } catch (e) {
              console.error('Error parsing device stream event:', e);
            }
          };

          eventSource.addEventListener('added', applyDeviceStreamEvent);
          eventSource.addEventListener('modified', applyDeviceStreamEvent);
          eventSource.addEventListener('error', (e) => {
            console.error('Device stream error:', e);
          });
        } catch (streamErr) {
          console.error('Failed to open device stream:', streamErr);
        }
      } catch (err) {
        error.value = err;
        console.error('Error fetching device details:', err);
      }
    }

    emit('update:ready');
  })

  // Watch for route changes to update active tab
  watch(() => router.currentRoute.value.path, () => {
    setSubLinks();
  });

  const isTemplateForCurrentDevice = (candidateTemplate) => {
    if (!candidateTemplate) return false;
    if (candidateTemplate.dataID && candidateTemplate.dataID === props.dataID) return true;

    const activeTemplateId = templateData.value?.id || deviceDetails.value?.defaultTemplate;
    return Boolean(activeTemplateId && candidateTemplate.id === activeTemplateId);
  };

  // Watch for template changes to update tabs
  watch(() => store.state.template, (newTemplate) => {
    if (isTemplateForCurrentDevice(newTemplate)) {
      templateData.value = newTemplate;
      setSubLinks();
    }
  }, { deep: true });

  onUnmounted(() => {
    if (subtitleUpdateInterval.value) {
      clearInterval(subtitleUpdateInterval.value);
    }
    if (typeof unsubscribeStream.value === 'function') {
      unsubscribeStream.value();
      unsubscribeStream.value = null;
    }
  })

  // Methods
  const getDeviceDetails = async () => {
    const response = await Device.get(`?dataID=${props.dataID}`);
    const device = { ...response.details, dataID: props.dataID };
    const prev = store.state.device?.dataID === props.dataID ? store.state.device : {};
    const merged = { ...prev, ...device };
    store.dispatch('setDevice', merged);
    return merged;
  }

  const getTemplate = async () => {
    try {
      const docSnap = doc(db, "templates", deviceDetails.value.defaultTemplate);
      const template = await getDoc(docSnap);

      if (template.exists()) {
        const data = template.data();
        data.id = template.id;
        data.dataID = props.dataID;

        // Store the template in vuex
        store.dispatch('setTemplate', data);
        
        // Return the data so it can be assigned to templateData.value
        return data;
      }
      return null;  
    } catch(error) {
      console.error('Failed to fetch template:', error);
      return null;
    }
  };

  const setSubLinks = async () => {
    if (!deviceDetails.value) {
      unitSecondaryTab.value = null;
      return;
    }

    const path = router.currentRoute.value.path;
    links.value.clear();

    // Generate tabs from template.pages[]
    if (templateData.value?.pages && Array.isArray(templateData.value.pages)) {
      // Sort pages by order field
      const sortedPages = [...templateData.value.pages].sort((a, b) => a.order - b.order);
      draggablePages.value = sortedPages;
      
      // Filter out invalid pages
      const validPages = sortedPages.filter(page => 
        page.id && page.id !== 'null' && page.id !== 'undefined'
      );
      
      for (const page of validPages) {
        const pageUrl = `/dashboard/${props.dataID}/p/${page.id}`;
        links.value.set(page.id, [pageUrl, path === pageUrl ? 'active' : 'inactive', page.name]);
      }
      
      // Log skipped pages with invalid IDs
      const invalidPages = sortedPages.filter(page => 
        !page.id || page.id === 'null' || page.id === 'undefined'
      );
      if (invalidPages.length > 0) {
        console.error('⚠️ INVALID PAGES DETECTED - Template:', templateData.value.id);
        invalidPages.forEach(page => console.error('Invalid page:', page));
        console.error('These pages will be skipped. Please fix the template in Firestore.');
      }
    } else {
      draggablePages.value = [];
    }

    if (isCloudAppPortalUnit(deviceDetails.value)) {
      const label = cloudAppPortalTabLabel(deviceDetails.value);
      const portalPath = cloudAppPortalPath(props.dataID);
      const isActive = path === portalPath;
      links.value.set(label, [portalPath, isActive ? 'active' : 'inactive']);
      unitSecondaryTab.value = { label, path: portalPath, active: isActive };
    } else {
      const mgrPath = unitManagementPath(props.dataID);
      const isActive = path === mgrPath;
      links.value.set('Management', [mgrPath, isActive ? 'active' : 'inactive']);
      unitSecondaryTab.value = { label: 'Management', path: mgrPath, active: isActive };
    }
  }

  const setSubtitle = () => {
    if (deviceDetails.value.friendlyName !== "") {
      internalTitle.value = deviceDetails.value.friendlyName;
      subtitle.value = `
        <p>
          <span style="font-weight: bold">Data ID: </span>
          ${props.dataID}
        </p>
        <p>
          <span style="font-weight: bold;">Last Active: </span>
          ${deviceDetails.value.lastActivityUTC ? timeSimplified(utcToLocal(deviceDetails.value.lastActivityUTC, timeFormat)) : 'Unknown'}
        </p>
      `.trim();
    } else {
      internalTitle.value = props.dataID;
      subtitle.value = `
        <p>
          <span style="font-weight: bold;">Last Active: </span>
          ${deviceDetails.value.lastActivityUTC ? timeSimplified(utcToLocal(deviceDetails.value.lastActivityUTC, timeFormat)) : 'Unknown'}
        </p>
      `.trim();
    }
  }

  const openAddPageModal = () => {
    showAddPageModal.value = true;
  };

  const getDashboardPageRoute = (pageId) => {
    const path = `/dashboard/${props.dataID}/p/${pageId}`;
    const mode = router.currentRoute.value.query?.mode;

    if (mode) {
      return { path, query: { ...router.currentRoute.value.query } };
    }

    return { path };
  };

  const handlePageAdded = async (newPage) => {
    showAddPageModal.value = false;
    
    // Refresh template from Firestore (assign locally so tabs update before route navigation)
    templateData.value = await getTemplate();
    setSubLinks();
    
    // Navigate to the new page
    router.push(`/dashboard/${props.dataID}/p/${newPage.id}`);
  };

  const handleRenamePage = (pageId) => {
    selectedPageId.value = pageId;
    const page = templateData.value.pages.find(p => p.id === pageId);
    newPageName.value = page?.name || '';
    showRenameModal.value = true;
  };

  const confirmRename = async () => {
    if (!newPageName.value.trim()) return;
    
    try {
      const templateRef = doc(db, 'templates', templateData.value.id);
      const freshTemplateSnap = await getDoc(templateRef);
      if (!freshTemplateSnap.exists()) {
        throw new Error('Template not found');
      }

      const freshTemplateData = freshTemplateSnap.data();
      const sourcePages = Array.isArray(freshTemplateData.pages) ? freshTemplateData.pages : [];

      const updatedPages = sourcePages.map(page => 
        page.id === selectedPageId.value 
          ? { ...page, name: newPageName.value.trim() }
          : page
      );
      
      await updateDoc(templateRef, { pages: updatedPages });
      
      templateData.value = await getTemplate();
      setSubLinks();
      
      showRenameModal.value = false;
      selectedPageId.value = null;
      newPageName.value = '';
    } catch (err) {
      console.error('Error renaming page:', err);
      alert('Failed to rename page. Please try again.');
    }
  };

  const handleDeletePage = (pageId) => {
    selectedPageId.value = pageId;
    showDeleteConfirm.value = true;
  };

  const confirmDelete = async () => {
    try {
      const updatedPages = templateData.value.pages
        .filter(page => page.id !== selectedPageId.value)
        .map((page, index) => ({ ...page, order: index }));
      
      const templateRef = doc(db, 'templates', templateData.value.id);
      await updateDoc(templateRef, { pages: updatedPages });
      
      await getTemplate();
      setSubLinks();
      
      // Navigate to first page or portal / management if no pages left
      if (updatedPages.length > 0) {
        router.push(`/dashboard/${props.dataID}/p/${updatedPages[0].id}`);
      } else {
        router.push(defaultUnitFallbackPath(props.dataID, deviceDetails.value));
      }
      
      showDeleteConfirm.value = false;
      selectedPageId.value = null;
    } catch (err) {
      console.error('Error deleting page:', err);
      alert('Failed to delete page. Please try again.');
    }
  };

  const handleTabsReorder = async () => {
    if (!isTemplateEditable.value) return;

    try {
      const updatedPages = draggablePages.value.map((page, index) => ({
        ...page,
        order: index
      }));
      
      const templateRef = doc(db, 'templates', templateData.value.id);
      await updateDoc(templateRef, { pages: updatedPages });
      
      await getTemplate();
      setSubLinks();
    } catch (err) {
      console.error('Error reordering pages:', err);
      alert('Failed to reorder pages. Please try again.');
    }
  };

</script>

<template>
  <div>
    <div class="flex flex-col md:flex-row justify-between h-fit p-4 md:p-0 bg-mesh-surface-page text-mesh-text-primary" :class="[
      deviceSubEntities.length > 0 && subtitle ? 'md:min-h-[130px]' : subtitle ? 'md:min-h-[100px]' : 'md:min-h-[65px]'
    ]">
      <!--Create the page title and subtitle-->
      <div class="inline-block">
        <h1 class="font-display font-black text-tier-4 leading-[1.0] md:m-[10px]">{{ internalTitle }}</h1>
<div v-if="deviceSubEntities.length > 0" class="md:ml-[10px] mb-2 flex flex-wrap gap-1">
          <span 
            v-for="subEntity in deviceSubEntities" 
            :key="subEntity.id"
            class="ui-badge"
          >
            {{ subEntity.name }}
          </span>
        </div>
        <div v-if="subtitle" v-html="subtitle" class="text-sm md:pl-2"></div>
      </div>

    <!--Create links to subpages-->
    <div v-if="links" class="flex overflow-x-auto md:overflow-x-visible md:self-end md:mr-20 mt-4 md:mt-0 -mx-4 md:mx-0 px-4 md:px-0 tab-scroll" role="navigation">
      <!-- Draggable Page Tabs -->
      <draggable 
        v-if="templateData && templateData.pages"
        v-model="draggablePages"
        @end="handleTabsReorder"
        item-key="id"
        class="flex flex-shrink-0"
        drag-class="opacity-0"
        :disabled="!isTemplateEditable"
        :animation="200"
        :delay="150"
        :delay-on-touch-only="true"
      >
        <template #item="{ element: page }">
          <div 
            :class="[
              router.currentRoute.value.path === `/dashboard/${props.dataID}/p/${page.id}`
                ? 'bg-mesh-surface-card text-mesh-text-primary border border-mc-steel border-b-0 -mb-px z-10'
                : 'bg-mesh-surface-page text-mesh-text-muted border border-mc-steel/40',
              'mr-2 md:ml-2 md:mr-0 rounded-lg md:rounded-t-mc-sm md:rounded-b-none inline-flex items-center relative',
              isTemplateEditable ? 'cursor-move' : 'cursor-default'
            ]"
          >
            <router-link 
              :to="getDashboardPageRoute(page.id)"
              class="p-2 whitespace-nowrap text-sm md:text-base flex-grow"
              :aria-current="router.currentRoute.value.path === `/dashboard/${props.dataID}/p/${page.id}` ? 'page' : undefined"
            >
              {{ page.name }}
            </router-link>
            
            <!-- Three-dot menu -->
            <Menu v-if="isTemplateEditable" as="div" class="relative pr-2" @mousedown.stop>
              <MenuButton 
                as="button"
                type="button"
                class="p-1 hover:bg-mc-cloud rounded-mc-sm transition-colors duration-160 inline-flex items-center focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40"
                @click.prevent.stop
              >
                <EllipsisVerticalIcon class="w-4 h-4" />
              </MenuButton>

              <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
              >
                <MenuItems 
                  class="absolute right-0 mt-2 w-48 origin-top-right rounded-mc-sm bg-mc-white border border-mc-steel shadow-mc-sm focus:outline-none z-[9999]"
                >
                  <div class="py-1">
                    <MenuItem v-slot="{ active }">
                      <button
                        type="button"
                        @click.prevent.stop="handleRenamePage(page.id)"
                        :class="[
                          active ? 'bg-mc-cloud text-mc-ink' : 'text-mc-ink',
                          'block w-full text-left px-4 py-2 text-sm'
                        ]"
                      >
                        Rename Page
                      </button>
                    </MenuItem>
                    <MenuItem v-slot="{ active }">
                      <button
                        type="button"
                        @click.prevent.stop="handleDeletePage(page.id)"
                        :class="[
                          active ? 'bg-mc-red-100 text-mc-red-700' : 'text-mc-red',
                          'block w-full text-left px-4 py-2 text-sm'
                        ]"
                      >
                        Delete Page
                      </button>
                    </MenuItem>
                  </div>
                </MenuItems>
              </transition>
            </Menu>
          </div>
        </template>
      </draggable>

      <!-- Portal or Management tab (always last, not draggable) -->
      <router-link
        v-if="unitSecondaryTab"
        :to="unitSecondaryTab.path"
        :class="[
          unitSecondaryTab.active
            ? 'bg-mesh-surface-card text-mesh-text-primary cursor-default border border-mc-steel border-b-0 -mb-px z-10'
            : 'bg-mesh-surface-page text-mesh-text-muted cursor-pointer border border-mc-steel/40',
          'whitespace-nowrap text-sm md:text-base'
        ]"
        class="p-2 ml-2 rounded-lg md:rounded-t-mc-sm md:rounded-b-none flex-shrink-0"
        :aria-current="unitSecondaryTab.active ? 'page' : undefined"
      >
        {{ unitSecondaryTab.label }}
      </router-link>

      <!-- Add Page Button -->
      <button
        v-if="templateData"
        @click="openAddPageModal"
        class="p-2 ml-2 cursor-pointer rounded-lg md:rounded-t-mc-sm md:rounded-b-none whitespace-nowrap text-sm md:text-base flex items-center gap-1 flex-shrink-0 border border-mc-steel/40 text-mesh-text-muted hover:bg-mc-steel/20 dark:hover:bg-white/[0.06] transition-colors duration-160"
        title="Add new page"
      >
        <PlusIcon class="w-4 h-4" />
      </button>
    </div>
  </div>

  <!-- Add Page Modal -->
  <AddDashboardPage
    v-if="showAddPageModal"
    :dataID="dataID"
    :templateId="templateData?.id"
    :existingPages="templateData?.pages || []"
    @close="showAddPageModal = false"
    @pageAdded="handlePageAdded"
  />

  <!-- Rename Page Modal -->
  <div v-if="showRenameModal" class="fixed top-0 left-0 right-0 bottom-0 bg-mc-ink/50 flex justify-center items-center z-50" @mousedown.self="renameBackdrop.onBackdropMousedown" @mouseup.self="renameBackdrop.onBackdropMouseup">
    <div class="ui-modal p-6 w-[500px]" @mousedown="renameBackdrop.onContentMousedown">
      <h2 class="font-display font-black text-xl text-center mb-6">Rename Page</h2>
      
      <form @submit.prevent="confirmRename">
        <div class="mb-6">
          <label class="font-bold block mb-2">
            New Page Name <span class="text-mc-red">*</span>
          </label>
          <input 
            v-model="newPageName" 
            type="text" 
            class="input-base p-3" 
            placeholder="Enter page name"
            required 
            autofocus
          />
        </div>

        <div class="flex justify-between items-center">
          <button 
            type="button" 
            @click="showRenameModal = false" 
            class="btn-secondary"
          >
            Cancel
          </button>
          <button 
            type="submit" 
            class="btn-primary"
          >
            Rename
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- Delete Confirmation Modal -->
  <div v-if="showDeleteConfirm" class="fixed top-0 left-0 right-0 bottom-0 bg-mc-ink/50 flex justify-center items-center z-50" @mousedown.self="deleteBackdrop.onBackdropMousedown" @mouseup.self="deleteBackdrop.onBackdropMouseup">
    <div class="ui-modal p-6 w-[500px]" @mousedown="deleteBackdrop.onContentMousedown">
      <h2 class="font-display font-black text-xl text-center mb-4">Delete Page</h2>
      <p class="text-center text-ink-muted mb-6">
        Are you sure you want to delete this page? This action cannot be undone.
      </p>
      
      <div class="flex justify-between items-center">
        <button 
          type="button" 
          @click="showDeleteConfirm = false" 
          class="btn-secondary"
        >
          Cancel
        </button>
        <button 
          type="button"
          @click="confirmDelete" 
          class="btn-primary px-6 py-2"
        >
          Delete
        </button>
      </div>
    </div>
  </div>
  </div>
</template>

<style scoped>
.tab-scroll {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.tab-scroll::-webkit-scrollbar {
  display: none;
}
</style>